import { __assign, __awaiter, __generator, __read } from "tslib";
import { PwaAccessTokenService } from '@office-start/auth-token-service';
import { AuthType, } from '@office-start/auth-types';
import { initializeComponentContext, useCurrentRoute, } from '@office-start/component-context-service';
import { resolveConfig } from '@office-start/config-resolver-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { getToken } from '@office-start/hybrid-auth-service';
import { getIdentity } from '@office-start/identity-service';
import { getLocStrings } from '@office-start/loc-service';
import { OfficeWorkload, } from '@office-start/officehome-types';
import { updatePerformanceRoute } from '@office-start/performance-service';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import * as Cookies from 'es-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { initializeOfficeInstrumentation } from './instrumentation/initializeOfficeInstrumentation';
var getPwaIdentity = function () { return __awaiter(void 0, void 0, void 0, function () {
    var startupConfig, reactShellConfig, authTypeString;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, resolveConfig('clientStartupConfiguration')];
            case 1:
                startupConfig = _a.sent();
                return [4 /*yield*/, resolveConfig('reactShellConfiguration')];
            case 2:
                reactShellConfig = _a.sent();
                if (!startupConfig || !reactShellConfig) {
                    // Todo: Investigate if reactShellConfig needs default value
                    console.error('No clientStartupConfig found');
                    throw new Error('Failed to resolve clientStartupConfiguration');
                }
                authTypeString = startupConfig.authTypeString;
                if (authTypeString === 'MSA') {
                    startupConfig.authType = AuthType.MSA;
                    // In PWA MSA, add puid and UPN retrieved from WAM to config.
                }
                else if (authTypeString === 'OrgId') {
                    startupConfig.authType = AuthType.OrgId;
                }
                else {
                    startupConfig.authType = AuthType.Unknown;
                }
                return [2 /*return*/, {
                        authType: startupConfig.authType,
                        authVersion: startupConfig.authVersion,
                        // Todo: See if we want to add encryptionConfig
                        // encryptionConfig: encryptionConfigService?.config,
                        isMsa: startupConfig.authType === AuthType.MSA,
                        isOrgId: startupConfig.authType === AuthType.OrgId,
                        isPipl: startupConfig.isPipl,
                        objectId: startupConfig.objectId,
                        puid: startupConfig.puid,
                        tenantId: startupConfig.tenantId,
                        userCountryCode: startupConfig.userCountryCode,
                        userPrincipalName: startupConfig.userPrincipalName,
                        identityIssuedTimeUnixMs: startupConfig.identityIssuedTimeUnixMs,
                        tenantRegionScope: startupConfig.tenantRegionScope,
                        dataBoundary: startupConfig.dataBoundary,
                        loginHint: startupConfig.loginHint,
                        activityTimeout: reactShellConfig.activityTimeout,
                        controlsClaim: reactShellConfig.controlsClaim,
                        signInState: reactShellConfig.signinState,
                        userDisplayName: reactShellConfig.userDisplayName,
                    }];
        }
    });
}); };
function getPwaAccessToken(params) {
    return __awaiter(this, void 0, void 0, function () {
        var pwaTokenService;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, PwaAccessTokenService.getInstance()];
                case 1:
                    pwaTokenService = _a.sent();
                    return [2 /*return*/, pwaTokenService.getAccessToken(params.tokenConsumer, params.forceFetch, params.resourceId, !params.silent, // allowInteractive parameter
                        params.capDialogData, params.clientId, // clientId parameter
                        params.scope, params.claims, params.claimsChallenge)];
            }
        });
    });
}
export function useCommonOfficeExperience(context, router, commonContext, isBootstrappedFromReactHarmony) {
    var _a, _b;
    var cdnDomain = context.cdnDomain, correlationId = context.correlationId, _c = context.officeHomeApiRoot, officeHomeApiRoot = _c === void 0 ? '' : _c, preferredCulture = context.preferredCulture, stringsContainerName = context.stringsContainerName;
    var workload = commonContext.workload;
    var featureGates = useFeatureGates().data;
    var scheduleOfficeInstrumentationInitEnabled = (_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.scheduleOfficeInstrumentationInitGates) === null || _b === void 0 ? void 0 : _b.scheduleOfficeInstrumentationInitEnabled;
    // TODO: Move some of this initialization code into a bootstrapper
    // Defer initialization until important operations are complete
    useEffect(function () {
        if (scheduleOfficeInstrumentationInitEnabled) {
            scheduleWork(function () {
                return initializeOfficeInstrumentation(context, workload, router);
            }, IDLE_VERYLOW_PRIORITY);
        }
        else {
            initializeOfficeInstrumentation(context, workload, router);
        }
    }, [context, workload, router, scheduleOfficeInstrumentationInitEnabled]);
    // Update the route for performance logging purposes
    var route = useCurrentRoute();
    useEffect(function () {
        if (route) {
            updatePerformanceRoute(route.route);
        }
    }, [route, router]);
    var getOfficeIdentity = useCallback(function () {
        if (workload === OfficeWorkload.Hwa) {
            return getPwaIdentity();
        }
        else {
            return getIdentity(correlationId, officeHomeApiRoot);
        }
    }, [correlationId, officeHomeApiRoot, workload]);
    var getAccessToken = useMemo(function () {
        return function (tokenConsumer, forceFetch, resourceUrl, scope, isBoot, claims, silent, capDialogData) {
            var tokenParam = {
                tokenConsumer: tokenConsumer,
                forceFetch: forceFetch,
                resourceId: resourceUrl,
                scope: scope,
                isBoot: isBoot,
                claims: claims,
                silent: silent,
                capDialogData: capDialogData,
            };
            if (workload === OfficeWorkload.Hwa) {
                return getPwaAccessToken(tokenParam);
            }
            else {
                return getToken(tokenParam);
            }
        };
    }, [workload]);
    var getCsrfToken = useCallback(function () { var _a; return (_a = Cookies.get('AjaxSessionKey')) !== null && _a !== void 0 ? _a : ''; }, []);
    var componentContext = useMemo(function () { return (__assign(__assign(__assign({}, context), { getAccessToken: getAccessToken, getCsrfToken: getCsrfToken, getIdentity: getOfficeIdentity, router: router, uiContext: {}, isBootstrappedFromReactHarmony: isBootstrappedFromReactHarmony }), commonContext)); }, [
        context,
        getAccessToken,
        getCsrfToken,
        getOfficeIdentity,
        router,
        isBootstrappedFromReactHarmony,
        commonContext,
    ]);
    var _d = __read(useState(null), 2), locStrings = _d[0], setLocStrings = _d[1];
    useEffect(function () {
        getLocStrings(cdnDomain, preferredCulture, stringsContainerName).then(setLocStrings);
    }, [cdnDomain, preferredCulture, stringsContainerName]);
    useEffect(function () {
        initializeComponentContext(componentContext);
    }, [componentContext]);
    return {
        componentContext: componentContext,
        locStrings: locStrings,
        setLocStrings: setLocStrings,
    };
}
//# sourceMappingURL=useCommonOfficeExperience.js.map